import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { waLink } from "@/lib/constants";
import { LeadFormFields } from "@/components/public/LeadFormFields";

const EMPTY = { first_name: "", last_name: "", phone: "", email: "", service_id: "", message: "", referral_source: "", preferred_date: "", preferred_time: "", consent: false };
const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const validate = (v, mode) => {
  const e = {};
  if (!v.first_name.trim()) e.first_name = "Ingresa tu nombre";
  if (!v.phone.trim()) e.phone = "Ingresa tu teléfono";
  else if (v.phone.replace(/\D/g, "").length < 7) e.phone = "Teléfono inválido";
  if (!v.email.trim()) e.email = "Ingresa tu correo";
  else if (!EMAIL_RX.test(v.email.trim())) e.email = "Correo inválido";
  if (mode === "agendar" && !v.preferred_date) e.preferred_date = "Selecciona una fecha";
  if (mode === "agendar" && !v.preferred_time) e.preferred_time = "Selecciona una hora";
  if (!v.consent) e.consent = "Debes aceptar para continuar";
  return e;
};

export const LeadFormDialog = ({ open, mode, serviceId, services, categories = [], center, onOpenChange, onModeChange }) => {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    if (open) {
      setValues((v) => ({ ...(status === "success" ? EMPTY : v), service_id: serviceId || "" }));
      setErrors({});
      setStatus("idle");
    }
  }, [open, serviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, val) => {
    setValues((v) => ({ ...v, [k]: val }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const errs = validate(values, mode);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("loading");
    try {
      const payload = { ...values, mode, service_id: values.service_id || null, referral_source: values.referral_source || null };
      if (mode !== "agendar") { payload.preferred_date = null; payload.preferred_time = null; }
      await api.post("/public/leads", payload);
      setStatus("success");
      setValues(EMPTY);
    } catch {
      setStatus("error");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-[1.5rem] border-slate-200 p-0 sm:max-w-xl" data-testid="lead-form-dialog">
        {status === "success" ? (
          <div className="flex flex-col items-center px-8 py-14 text-center" data-testid="lead-form-success">
            <span className="grid h-16 w-16 place-items-center rounded-full bg-[#14B8A6]/10 text-[#14B8A6]"><CheckCircle2 className="h-8 w-8" /></span>
            <DialogTitle className="mt-6 font-display text-2xl font-extrabold text-[#0B2545]">¡Gracias! Hemos recibido tu solicitud.</DialogTitle>
            <DialogDescription className="mt-3 max-w-sm text-base text-slate-600">
              Un asesor de Centro Médico IEMED te contactará muy pronto.
            </DialogDescription>
            <Button onClick={() => onOpenChange(false)} className="mt-8 rounded-full bg-[#0B2545] px-8" data-testid="lead-form-close-button">Cerrar</Button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="p-6 sm:p-8" data-testid="lead-form">
            <DialogHeader className="text-left">
              <DialogTitle className="font-display text-2xl font-extrabold text-[#0B2545]" data-testid="lead-form-title">
                {mode === "agendar" ? "Agendar una cita" : "Solicitar información"}
              </DialogTitle>
              <DialogDescription>Completa tus datos y te contactaremos.</DialogDescription>
            </DialogHeader>
            <div className="mt-5 inline-flex rounded-full bg-[#F5F7FA] p-1" role="tablist">
              {[["info", "Información"], ["agendar", "Agendar cita"]].map(([m, label]) => (
                <button key={m} type="button" onClick={() => onModeChange(m)}
                        className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${mode === m ? "bg-white text-[#1E6FD9] shadow-sm" : "text-slate-500"}`}
                        data-testid={`lead-form-mode-${m}`}>{label}</button>
              ))}
            </div>
            <LeadFormFields values={values} errors={errors} set={set} services={services} categories={categories} mode={mode} />
            <label className="mt-5 flex cursor-pointer items-start gap-3 text-sm text-slate-600">
              <Checkbox checked={values.consent} onCheckedChange={(c) => set("consent", c === true)} className="mt-0.5" data-testid="lead-form-consent" />
              Acepto que Centro Médico IEMED me contacte y trate mis datos para atender mi solicitud.
            </label>
            {errors.consent && <p className="mt-1.5 text-xs text-rose-600" data-testid="lead-form-error-consent">{errors.consent}</p>}
            {status === "error" && (
              <div className="mt-5 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="lead-form-server-error">
                No pudimos enviar tu solicitud. Inténtalo de nuevo o escríbenos por WhatsApp.
                <a href={waLink(center.whatsapp)} target="_blank" rel="noopener noreferrer" className="mt-2 flex items-center gap-1.5 font-semibold">
                  <MessageCircle className="h-4 w-4" /> Escribir por WhatsApp
                </a>
              </div>
            )}
            <Button type="submit" disabled={status === "loading"} className="mt-6 h-12 w-full rounded-full bg-[#1E6FD9] text-base hover:bg-[#0B2545]"
                    data-testid="lead-form-submit-button">
              {status === "loading" ? <Loader2 className="h-5 w-5 animate-spin" /> : mode === "agendar" ? "Solicitar cita" : "Enviar solicitud"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
